import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Divider,
  Link,
  List,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  blockquote: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    borderLeft: `4px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  },
  inlineCode: {
    padding: theme.spacing(1),
    borderRadius: 4,
    background: theme.palette.action.hover,
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
    fontSize: '0.85em',
  },
  codeBlockWrapper: {
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
    overflow: 'auto',
  },
  codeBlock: {
    margin: 0,
    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
    fontSize: '0.8rem',
  },
  list: {
    listStyleType: 'disc',
    paddingLeft: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  orderedList: {
    listStyleType: 'decimal',
    paddingLeft: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  link: {
    maxWidth: '100%',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    wordWrap: 'break-word',
  },
  listItem: {
    margin: theme.spacing(0.25, 0),
    padding: 0,
  },
  table: {
    margin: theme.spacing(2, 0),
    border: `1px solid ${theme.palette.divider}`,
  },
  tableCellHead: {
    fontWeight: 600,
    background: theme.palette.action.hover,
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:first-child': {
      borderLeft: 'none',
    },
    '&:last-child': {
      borderRight: 'none',
    },
  },
  tableCell: {
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    '&:first-child': {
      borderLeft: 'none',
    },
    '&:last-child': {
      borderRight: 'none',
    },
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

interface IProps {
  children: string;
}

const MarkdownRenderer = ({ children }: IProps) => {
  const classes = useStyles();

  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={{
        h1: ({ children, node, ...props }: any) => (
          <Typography {...props} gutterBottom variant="h3" component="h1">
            {children}
          </Typography>
        ),
        h2: ({ children, node, ...props }: any) => (
          <Typography {...props} gutterBottom variant="h4" component="h2">
            {children}
          </Typography>
        ),
        h3: ({ children, node, ...props }: any) => (
          <Typography {...props} gutterBottom variant="h5" component="h3">
            {children}
          </Typography>
        ),
        h4: ({ children, node, ...props }: any) => (
          <Typography {...props} gutterBottom variant="h6" component="h4">
            {children}
          </Typography>
        ),
        h5: ({ children, node, ...props }: any) => (
          <Typography {...props} gutterBottom variant="body1" component="h5">
            {children}
          </Typography>
        ),
        h6: ({ children, node, ...props }: any) => (
          <Typography {...props} gutterBottom variant="body2" component="h6">
            {children}
          </Typography>
        ),
        p: ({ children, node, ...props }: any) => (
          <Typography {...props} paragraph variant="body1" component="p">
            {children}
          </Typography>
        ),
        a: ({ children, node, href, ...props }: any) => (
          <Link {...props} href={href} target="_blank" rel="noopener noreferrer" className={classes.link}>
            {children}
          </Link>
        ),
        ul: ({ children, node, ...props }: any) => (
          <List {...props} className={classes.list}>
            {children}
          </List>
        ),
        ol: ({ children, node, ...props }: any) => (
          <List {...props} component="ol" className={classes.orderedList}>
            {children}
          </List>
        ),
        li: ({ children, node, ...props }: any) => (
          <li {...props} className={classes.listItem}>
            {children}
          </li>
        ),
        blockquote: ({ children, node, ...props }: any) => (
          <Paper {...props} elevation={0} className={classes.blockquote}>
            {children}
          </Paper>
        ),
        hr: () => <Divider style={{ margin: '16px 0' }} />,
        code: ({ inline, className, children: codeChildren }: any) => {
          if (inline) {
            return <code className={classes.inlineCode}>{codeChildren}</code>;
          }
          return (
            <Paper variant="outlined" className={classes.codeBlockWrapper}>
              <Box component="pre" className={classes.codeBlock}>
                <code className={className}>{codeChildren}</code>
              </Box>
            </Paper>
          );
        },
        pre: ({ children }: any) => (
          <Paper variant="outlined" className={classes.codeBlockWrapper}>
            <Box component="pre" className={classes.codeBlock}>
              {children}
            </Box>
          </Paper>
        ),
        table: ({ children, node, ...props }: any) => (
          <Table {...props} size="small" className={classes.table}>
            {children}
          </Table>
        ),
        thead: ({ children, node, ...props }: any) => <TableHead {...props}>{children}</TableHead>,
        tbody: ({ children, node, ...props }: any) => <TableBody {...props}>{children}</TableBody>,
        tr: ({ children, node, ...props }: any) => <TableRow {...props}>{children}</TableRow>,
        th: ({ children, node, ...props }: any) => (
          <TableCell component="th" className={classes.tableCellHead} {...props}>
            {children}
          </TableCell>
        ),
        td: ({ children, node, ...props }: any) => (
          <TableCell className={classes.tableCell} {...props}>
            {children}
          </TableCell>
        ),
      }}
    >
      {children}
    </Markdown>
  );
};

export default MarkdownRenderer;
